import { Box, Button } from '@mui/material';
import { forwardRef, useCallback } from 'react';
import { chartPanelSubmitButtonSx, chartPanelSubmitButtonWrapperSx } from '../styles/chartReader.styles';

type ChartPanelSubmitButtonProps = {
    /** Disabled until the caller's selector has a valid pending selection. */
    disabled: boolean;
    /** Dynamic per the cfra-accessibility-standards Action Button pattern —
     *  reflects the pending selection when enabled, or what's still needed
     *  when disabled. */
    ariaLabel: string;
    onSubmit: () => void;
    /** Called when Tab (not Shift+Tab) is pressed while Submit has focus.
     *  Submit is the last control in the selection sequence, so a plain Tab
     *  here would otherwise leave the panel entirely (e.g. into the page
     *  footer) — callers use this to loop focus back to the first control
     *  instead, keeping repeated look-ups inside the same selector. */
    onTabAway?: () => void;
};

/**
 * Right-aligned Submit button shared by DataPointSelector and the
 * SnapshotValueSelector-based selectors (Sector/AssetAllocation/
 * CreditQuality/SummaryCreditRating) — confirms the user's pending pick,
 * which is what fires the parent's `onSelected*Change` callback (WCAG 2.2
 * 3.2.2 On Input).
 */
export const ChartPanelSubmitButton = forwardRef<HTMLButtonElement, ChartPanelSubmitButtonProps>(
    ({ disabled, ariaLabel, onSubmit, onTabAway }, ref) => {
        const handleKeyDown = useCallback(
            (event: React.KeyboardEvent<HTMLButtonElement>) => {
                if (onTabAway && event.key === 'Tab' && !event.shiftKey) {
                    event.preventDefault();
                    onTabAway();
                }
            },
            [onTabAway],
        );

        return (
            <Box sx={chartPanelSubmitButtonWrapperSx}>
                <Button
                    ref={ref}
                    variant='contained'
                    disabled={disabled}
                    onClick={onSubmit}
                    onKeyDown={handleKeyDown}
                    aria-label={ariaLabel}
                    disableElevation
                    sx={chartPanelSubmitButtonSx}>
                    Submit
                </Button>
            </Box>
        );
    },
);

ChartPanelSubmitButton.displayName = 'ChartPanelSubmitButton';
